import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Advantage, AvailableSchedule, ClassroomCourse, ClassroomCourseFilter } from '../../models';
import { IBanner } from '../../models/banner';
import { Feedback } from '../../models/feedback';
import { Question } from '../../models/question';
import { BaseApiService } from '@core/http/base.api.service';
import { ConfigService } from '@core/services/config.service';
import { PageList } from '@core/models/page-list';
import { Guid } from '@core/utils/guid';
import { Review } from '@shared/models/review';
import { Participant } from '../../models/participant';

@Injectable({
  providedIn: 'root'
})
export class ClassroomCourseApiService extends BaseApiService {
  constructor(public http: HttpClient, private configService: ConfigService) {
    super(http);
  }

  get baseUrl(): string {
    const config = this.configService.getCurrent();
    return `${config.services.classroom}/api/classroom`;
  }

  get YouthBaseUrl(): string {
    const config = this.configService.getCurrent();
    return `${config.services.classroom}/api/youth`;
  }

  get bookingUrl(): string {
    const config = this.configService.getCurrent();
    return `${config.services.booking}/api/booking`;
  }

  get userUrl(): string {
    const config = this.configService.getCurrent();
    return `${config.services.identity}/api/user`;
  }

  getAllClassroomCourse(filter = new ClassroomCourseFilter()): Observable<PageList<ClassroomCourse>> {
    const params = new HttpParams()
      .append('search', filter.search)
      .append('onlyPublished', String(filter.onlyPublished))
      .append('onlyPending', String(filter.onlyPending))
      .append('onlyWithAvailableSlot', filter.onlyWithAvailableSlot)
      .append('sort', filter?.sort ?? '')
      .append('pageOffset', String(filter.pageIndex))
      .append('pageSize', String(filter.pageSize))
      .append('dateFilter', String(filter.dateFilter))
      .append('regions', filter?.locations?.join(',') || '' )
      .append('categories', filter.categories || '')
      .append('templateId', filter?.templateId ?? '');

    return super.getAllWithPagination<ClassroomCourse>(this.baseUrl, params);
  }

  getAllYouthCourse(filter = new ClassroomCourseFilter()): Observable<PageList<ClassroomCourse>> {
    const params = new HttpParams()
      .append('search', filter.search)
      .append('onlyPublished', String(filter.onlyPublished))
      .append('onlyPending', String(filter.onlyPending))
      .append('onlyWithAvailableSlot', filter.onlyWithAvailableSlot)
      .append('sort', filter?.sort ?? '')
      .append('pageOffset', String(filter.pageIndex))
      .append('pageSize', String(filter.pageSize))
      .append('dateFilter', String(filter.dateFilter))
      .append('regions', filter?.locations?.join(',') || '' )
      .append('categories', filter.categories || '')
      .append('templateId', filter?.templateId ?? '');

    return super.getAllWithPagination<ClassroomCourse>(this.YouthBaseUrl, params);
  }



  getClassroomCourse(id: string): Observable<ClassroomCourse> {
    if (Guid.IsGuid(id)) {
      return this.http.get<ClassroomCourse>(`${this.baseUrl}/${id}`).pipe(map((response) => response));
    } else {
      return this.http.get<ClassroomCourse>(`${this.baseUrl}/${id}`).pipe(map((response) => response));
    }
  }

  getAvailableSchedule(courseId: string): Observable<AvailableSchedule[]> {
    return this.http.get<AvailableSchedule[]>(`${this.bookingUrl}/check/${courseId}`);
  }

  fetchInstructorCourses(instructorId: string): Observable<ClassroomCourse[]> {
    const params = new HttpParams()
      .append('instructorId', instructorId)
      .append('onlyPublished', 'true')
      .append('sort', 'ratting desc')
      .append('pageOffset', '0')
      .append('pageSize', '100');

    return this.http.get<ClassroomCourse[]>(`${this.baseUrl}`, { params });
  }

  getClassroomCourseReviews(
    id: string,
    pageOffset: number = 0,
    pageSize: number = 3,
    sort: string = 'rating desc'
  ): Observable<PageList<Review>> {
    const params = new HttpParams()
      .append('pageOffset', pageOffset.toString())
      .append('pageSize', pageSize.toString())
      .append('sort', sort);

    return super.getAllWithPagination<Review>(`${this.baseUrl}/${id}/review`, params);
  }

  sendCourseReview(id: string, review: Review): Observable<Review> {
    return this.http.post<Review>(`${this.baseUrl}/${id}/review`, review);
  }

  sendCourseFeedback(id: string, feedback: Feedback): Observable<Feedback> {
    return this.http.post<Feedback>(`${this.baseUrl}/${id}/feedback`, feedback);
  }

  getCourseAdvantage(courseId: string): Observable<Advantage[]> {
    return this.http.get<Advantage[]>(`${this.baseUrl}/${courseId}/getadvantages`);
  }

  getCourseFaq(courseId: string): Observable<Question[]> {
    return this.http.get<Question[]>(`${this.baseUrl}/${courseId}/questions`);
  }

  isUserReview(userId: string, courseId: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}/${courseId}/getreview?userId=${userId}`);
  }

  getExplorePromotionalBanners() {
    const config = this.configService.getCurrent();
    const baseUrl = `${config.services.course}/api/banner`;
    return this.http.get<IBanner[]>(baseUrl);
  }

  checkPartnerIdentity(phone: string): Observable<Participant> {
    return this.http.post<Participant>(`${this.userUrl}/getUserInfoByPhoneOrEmail`,{ phone });
  }

  decreaseScheduleCount(courseId: string, scheduleId: number, numOfBooking: number): Observable<ClassroomCourse> {
    const params = new HttpParams()
      .append('classroomCourseId', courseId)
      .append('scheduleId', scheduleId)
      .append('numberOfBookings', numOfBooking)

    return this.http.put<ClassroomCourse>(`${this.baseUrl}/decreaseScheduleCount`, {}, { params });
  }
}
